/**
 * Format content analysis description into structured bullet points
 * Parses text and creates organized sections with colored bold titles
 */

import React from 'react';

export const FormattedContentAnalysis = ({ content, darkMode = false }) => {
  if (!content) return null;

  // Parse content into sections
  const parseContent = (text) => {
    const sections = [];
    
    // Split by common section patterns
    const lines = text.split('\n');
    let currentSection = null;
    
    lines.forEach(line => {
      const trimmed = line.trim();
      if (!trimmed) return;

      // Detect section headers (lines ending with colon or all caps)
      const isHeader = trimmed.endsWith(':') || 
                      (trimmed === trimmed.toUpperCase() && trimmed.length > 5) ||
                      trimmed.match(/^(Visual|Audio|Dialogue|Scene|Frame|Emotion|Timeline|Hook|Key|Pacing|CTA|Opening|Middle|End|Beginning|Conclusion)/i);

      if (isHeader) {
        // Start new section
        currentSection = {
          title: trimmed.replace(/:$/, ''),
          points: []
        };
        sections.push(currentSection);
      } else if (currentSection) {
        // Add to current section
        currentSection.points.push(trimmed);
      } else {
        // No section yet, create a general one
        if (sections.length === 0) {
          currentSection = { title: 'Overview', points: [] };
          sections.push(currentSection);
        }
        sections[sections.length - 1].points.push(trimmed);
      }
    });

    // If no sections found, create single section
    if (sections.length === 0) {
      sections.push({
        title: 'Analysis',
        points: text.split(/[.!?]+/).filter(s => s.trim().length > 10).map(s => s.trim())
      });
    }

    return sections;
  };

  const sections = parseContent(content);

  // Color palette for section titles
  const colors = [
    'text-blue-600',
    'text-purple-600', 
    'text-green-600',
    'text-orange-600',
    'text-pink-600',
    'text-indigo-600',
    'text-teal-600',
    'text-red-600'
  ];

  return (
    <div className="space-y-4">
      {sections.map((section, idx) => (
        <div key={idx} className={`p-4 rounded-lg ${darkMode ? 'bg-gray-800/50' : 'bg-gray-50'}`}>
          <h4 className={`font-bold text-lg mb-2 ${colors[idx % colors.length]}`}>
            {section.title}
          </h4>
          <ul className={`space-y-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            {section.points.map((point, pointIdx) => (
              <li key={pointIdx} className="flex items-start">
                <span className={`mr-2 mt-1 ${colors[idx % colors.length]}`}>•</span>
                <span className="flex-1 leading-relaxed">{point}</span>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
};

export default FormattedContentAnalysis;
